const DEFAULT_UPDATE_INTERVAL_MS = 60_000

type UpdateRegistration = Pick<ServiceWorkerRegistration, 'update'>

interface UpdateCheckOptions {
  intervalMs?: number
  windowTarget?: Pick<Window, 'addEventListener' | 'removeEventListener' | 'setInterval' | 'clearInterval'>
  documentTarget?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>
}

export function installServiceWorkerUpdateChecks(
  registration: UpdateRegistration,
  options: UpdateCheckOptions = {},
) {
  const windowTarget = options.windowTarget ?? window
  const documentTarget = options.documentTarget ?? document
  const intervalMs = options.intervalMs ?? DEFAULT_UPDATE_INTERVAL_MS
  let checking = false

  const checkForUpdate = async () => {
    if (checking) return
    checking = true
    try {
      await registration.update()
    } catch {
      // A failed update check must not interrupt the current offline-capable session.
    } finally {
      checking = false
    }
  }

  const onFocus = () => void checkForUpdate()
  const onVisibilityChange = () => {
    if (documentTarget.visibilityState === 'visible') void checkForUpdate()
  }

  windowTarget.addEventListener('focus', onFocus)
  documentTarget.addEventListener('visibilitychange', onVisibilityChange)
  const interval = windowTarget.setInterval(() => void checkForUpdate(), intervalMs)
  void checkForUpdate()

  return () => {
    windowTarget.removeEventListener('focus', onFocus)
    documentTarget.removeEventListener('visibilitychange', onVisibilityChange)
    windowTarget.clearInterval(interval)
  }
}
